import { useState } from 'react';

import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import { removeCurrentUserDevice } from 'src/services/apiRequests/DeviceManagementService';
import { resetSettings } from 'src/services/apiRequests/settingsService';
import { getTranslations } from 'src/services/apiRequests/TranslationsService';
import { UsersPreferenceData } from 'src/store/interface';
import { mixpanelObject } from 'src/utils/analytics';
import {
  clearCtaEventsProperty,
  settingsPropertySetter
} from 'src/utils/analytics/mixpanel/mixpanelHelper';
import { logout } from 'src/utils/auth-utils/LogoutHandler';
import {
  getLocalStorage,
  setCookie,
  setLocalStorage
} from 'src/utils/browserStorageHandler';
import { DISPLAY_LANGUAGE_COOKIE } from 'src/utils/constants/cookieConstants';
import { SETTINGS } from 'src/utils/constants/localStorageConstants';
import {
  HamburgerMenuLinks,
  ProfileMenuLinks
} from 'src/utils/constants/RouterConstants';
import { getGuestUserToken } from 'src/utils/UtilityMethods';

import { useAppStore } from 'src/hooks/useAppStore';
import SimpleStore from '../../../store/SimpleStore';
import {
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_ELEMENT,
  MIXPANEL_HAMBURGER,
  MIXPANEL_PAGE_NAME,
  MIXPANEL_RESET_SETTINGS_TO_DEFAULT,
  MIXPANEL_SETTING_CHANGED
} from '../../../utils/analytics/mixpanel/mixpanelConstants';
import {
  ctaEvent,
  defaultSettingRestoredEvent,
  timeEvent
} from '../../../utils/analytics/mixpanel/mixpanelEventsHelper';
import { HTTP_URL_PARAMS } from '../../../utils/constants/HttpMethodsConstants';
import {
  DisplayLanguage,
  HamburgerMenuAlt,
  HamburgerMenuUrls,
  pageNameConstants
} from '../../../utils/constants/stringConstants';
import {
  activeItem,
  ChevronDown,
  ChevronUp,
  menuGroup,
  menuItemStyle,
  menuTitle,
  NewOption,
  slideDown
} from './HamburgerMenuGroupStyle';

/**
 * Hamburger Menu props
 */
export type ItemsType =
  | {
      name: string;
      link: string;
      external?: boolean;
      isActive: boolean;
    }
  | undefined;
type HamburgerMenuGroupProps = {
  name: string;
  show: boolean;
  items: ItemsType[];
  onClick: (name: string) => void;
  hamburgerMenuCLickHandler: () => void;
};

const MenuGroup = styled.div`
  ${menuGroup}
`;

const MenuTitle = styled.div`
  ${menuTitle};
`;

const HamburgerMenuGroup = ({
  show,
  name,
  items,
  onClick,
  hamburgerMenuCLickHandler
}: HamburgerMenuGroupProps) => {
  const id = name
    ? `hamburger-menu-${name.replaceAll(' ', '-').toLowerCase()}`
    : 'hamburger-menu';
  // state hooks
  const [showItem, toggleShowItem] = useState(show);

  const {
    setParentalData,
    setUsersPreferenceData,
    setPopups,
    setTranslations
  } = useAppStore();
  const router = useRouter();

  // methods starts here

  /**
   * Toggle the accordian
   * @param event mouse click event
   */
  const handleOnClick = (event: MouseEvent): void => {
    event.stopPropagation();
    toggleShowItem((prevState) => !prevState);
    onClick(name);
  };

  const resetToDefaultMixpanelEvent = (
    newUserSettings: UsersPreferenceData
  ) => {
    mixpanelObject.setProperty(
      MIXPANEL_ELEMENT,
      MIXPANEL_RESET_SETTINGS_TO_DEFAULT
    );
    mixpanelObject.setProperty(
      MIXPANEL_SETTING_CHANGED,
      'Default Setting Restored'
    );
    settingsPropertySetter(mixpanelObject, newUserSettings);
    defaultSettingRestoredEvent();
  };

  const resetSettingsData = async () => {
    const userSettings = JSON.parse(getLocalStorage(SETTINGS));
    const { ENGLISH } = DisplayLanguage;
    // only auto_play, display_language and streaming_quality will reset to default remaining will be same
    const settingsObj = {
      auto_play: true,
      display_language: ENGLISH,
      streaming_quality: 'Auto'
    };
    const newUserSettings = {
      ...userSettings,
      ...settingsObj
    };

    resetSettings(settingsObj);
    setUsersPreferenceData({ ...newUserSettings });
    setCookie(DISPLAY_LANGUAGE_COOKIE, ENGLISH);
    setLocalStorage(SETTINGS, newUserSettings);
    resetToDefaultMixpanelEvent(newUserSettings);
    SimpleStore.set(
      HTTP_URL_PARAMS.DISPLAY_LANGUAGE,
      newUserSettings.display_language
    );
    SimpleStore.set(
      HTTP_URL_PARAMS.CONTENT_LANGUAGE,
      newUserSettings.content_language
    );
    const pathname = router.asPath;
    const translations = await getTranslations(ENGLISH);
    setTranslations(translations);
    router.push(pathname, pathname, {
      locale: ENGLISH
    });
  };

  const languageSelectionPopupHandler = () => {
    //TODO: Integrate with the store
    setPopups({ showLanguagePopup: true });
  };

  // global oneTrust popup enable
  const consentSettings = () => {
    if (window?.OneTrust) {
      window.OneTrust.ToggleInfoDisplay();
    }
  };

  const logoutHandler = async () => {
    const path = router.asPath !== '/myaccount/payment' ? router.asPath : '/';
    window.location.assign(path);
    logout();
    removeCurrentUserDevice();
    await getGuestUserToken();
  };

  const burgerClickMixpanelEvent = (element: string, link: string) => {
    clearCtaEventsProperty(mixpanelObject);
    mixpanelObject.setProperty(MIXPANEL_ELEMENT, element || 'N/A');
    mixpanelObject.setProperty(MIXPANEL_BUTTON_TYPE, MIXPANEL_HAMBURGER);
    if (link === HamburgerMenuLinks.REFERRAL_ROUTE) {
      mixpanelObject.setProperty(MIXPANEL_PAGE_NAME, pageNameConstants.MORE);
    }
    ctaEvent();
    timeEvent();
  };

  const menuItemOnClick = (
    event: any,
    name: string,
    link: string,
    external = false
  ) => {
    event.stopPropagation();
    event.preventDefault();
    hamburgerMenuCLickHandler();
    burgerClickMixpanelEvent(name, link);
    if (external) {
      window.open(link, '_blank');
    }
    switch (link) {
      case HamburgerMenuLinks.PARENTAL_CONTROL:
        if (router.asPath !== '/parentalcontrol') {
          setParentalData({ showParentalPinPageFlag: true });
        }
        break;
      case HamburgerMenuLinks.RESET_SETTINGS:
        resetSettingsData();
        break;
      case HamburgerMenuLinks.LANGUAGE:
        languageSelectionPopupHandler();
        break;
      case HamburgerMenuLinks.CONSENT_SETTINGS:
        consentSettings();
        break;
      case ProfileMenuLinks.LOGOUT_LINK:
        logoutHandler();
        break;
      default:
        window.location.href = link;
        break;
    }
  };

  return (
    <MenuGroup key={name}>
      {name ? (
        <MenuTitle
          onClick={(event: any) => handleOnClick(event)}
          data-testid={id}>
          <span>{name}</span>
          <img
            css={showItem ? ChevronDown : ChevronUp}
            src={HamburgerMenuUrls.CHEVRON_ICON}
            alt={HamburgerMenuAlt.CHEVRON_ICON}></img>
        </MenuTitle>
      ) : null}

      {showItem && (
        <>
          {items.map((menuItem) => {
            if (menuItem) {
              return menuItem.link &&
                menuItem.link !== HamburgerMenuLinks.LANGUAGE ? (
                <a
                  href={menuItem.link}
                  css={[
                    menuItemStyle(name),
                    showItem ? slideDown : slideDown,
                    menuItem.isActive && activeItem
                  ]}
                  key={menuItem.name}
                  onClick={(event) =>
                    menuItemOnClick(
                      event,
                      menuItem.name,
                      menuItem.link,
                      menuItem.external
                    )
                  }
                  data-testid={`${id}-${menuItem.name
                    .replaceAll(' ', '-')
                    .toLowerCase()}`}>
                  {menuItem.name}
                  {menuItem.link === HamburgerMenuLinks.NFT ? (
                    <NewOption> </NewOption>
                  ) : null}
                </a>
              ) : (
                <div
                  css={[
                    menuItemStyle(name),
                    showItem ? slideDown : slideDown,
                    menuItem.isActive && activeItem
                  ]}
                  key={menuItem.name}
                  onClick={(event) =>
                    menuItemOnClick(
                      event,
                      menuItem.name,
                      menuItem.link,
                      menuItem.external
                    )
                  }
                  data-testid={`${id}-${menuItem.name
                    .replaceAll(' ', '-')
                    .toLowerCase()}`}>
                  {menuItem.name}
                </div>
              );
            }
          })}
        </>
      )}
    </MenuGroup>
  );
};

export default HamburgerMenuGroup;
